<!-- Modal version of ConfirmIdentityForm -->
<template>
	<RestartEncryptionModal
		:plan="plan"
		v-model="resetModalOpen"
	/>
	<InlineModal
		id="confirm-identity-modal"
		:open="currentPlanIsEncrypted"
		variant="small"
		class="z-50"
	>
		<template #header>
			<div
				class="mb-2 w-full items-center gap-4 text-center font-bold capitalize"
			>
				{{
					t('encryption.confirmIdentity.heading', {
						firstName: plan.firstName,
						lastName: plan.lastName,
					})
				}}
			</div>
		</template>
		<template #default>
			<form @submit.prevent="decryptUser">
				<div class="flex w-full flex-col items-center justify-around">
					<p
						class="mt-4 px-8 text-center font-sans text-sm leading-tight font-normal text-slate-500!"
					>
						{{ t('encryption.confirmIdentity.body') }}
					</p>
					<FormKit
						type="text"
						:label="t('encryption.confirmIdentity.inputLabel')"
						outer-class="w-full mt-6 mb-0! max-w-full!"
						:class="[errorMessage && 'border-red-300 text-red-950!']"
						:placeholder="t('encryption.confirmIdentity.inputPlaceholder')"
						v-model="secretValue"
						maxlength="11"
					/>
					<div
						v-if="errorMessage"
						class="mt-2 self-start text-sm font-medium text-red-500!"
					>
						{{ errorMessage }}
					</div>
					<button
						type="submit"
						:disabled="!formValid || inProgress"
						class="mt-12 w-full rounded-lg px-16 py-2 font-bold"
						:class="[
							formValid && !inProgress
								? 'bg-accent-400! text-accent-700!'
								: 'text-secondary-400! bg-slate-200!',
						]"
					>
						{{ t('encryption.confirmIdentity.submit') }}
					</button>
					<i18n-t
						keypath="encryption.confirmIdentity.restart"
						tag="p"
					>
						<button
							type="button"
							@click="resetModalOpen = true"
							class="pt-4 font-bold text-red-500! underline"
						>
							{{ t('encryption.confirmIdentity.restartButton') }}
						</button>
					</i18n-t>
				</div>
			</form>
		</template>
	</InlineModal>
</template>

<script setup lang="ts">
import InlineModal from '../common/InlineModal.vue'
import RestartEncryptionModal from './RestartEncryptionModal.vue'
import type Plan from '@/classes/Plan'
import { usePresentationLocale } from '@/i18n'
import { useConfirmIdentity } from '@/mixins/confirmIdentity'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import { type PropType, ref, watch } from 'vue'

const { t } = usePresentationLocale()

const props = defineProps({
	plan: {
		type: Object as PropType<Plan>,
		required: true,
	},
	currentPlanIsEncrypted: {
		type: Boolean,
		required: true,
	},
})

const sessionDocumentsStore = useSessionDocumentsStore()
const resetModalOpen = ref(false)

watch(
	() => props.plan,
	(newVal) => {
		if (newVal) {
			secretValue.value = ''
			errorMessage.value = ''
		}
	}
)

const emit = defineEmits(['decryptUser'])
const { errorMessage, secretValue, formValid, inProgress, submitForm } =
	useConfirmIdentity()

const decryptUser = async () => {
	await submitForm(props.plan)
	if (!sessionDocumentsStore.currentPlanIsEncrypted) {
		emit('decryptUser')
	}
}
</script>

<style>
#confirm-identity-modal .inline-modal-backdrop {
	background-image: url('@/assets/blurry_document_small.png');
	background-size: 120%;
}
</style>
